import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface InteractionStat {
  contentId: number;
  contentName: string;
  successRate: number;
  attemptCount: number;
}

interface TopInteractionsCardProps {
  interactions: InteractionStat[];
  onInteractionClick?: (contentId: number, contentName: string) => void;
  isLoading?: boolean;
}

/**
 * Card showing top interactions quick insights and aggregated stats
 */
const TopInteractionsCard = ({
  interactions,
  onInteractionClick,
  isLoading,
}: TopInteractionsCardProps) => {
  const { t } = useTranslation();

  // Calculate aggregated stats and extract key interactions
  const insights = useMemo(() => {
    if (interactions.length === 0) return null;

    // Aggregated stats
    const totalInteractions = interactions.length;
    const totalAttempts = interactions.reduce((sum, i) => sum + i.attemptCount, 0);
    const avgSuccessRate =
      interactions.reduce((sum, i) => sum + i.successRate * i.attemptCount, 0) / totalAttempts;

    // Sort by success rate
    const sortedBySuccess = [...interactions].sort((a, b) => a.successRate - b.successRate);
    const worst = sortedBySuccess[0];
    const best = sortedBySuccess[sortedBySuccess.length - 1];

    // Sort by attempt count (most attempted)
    const sortedByAttempts = [...interactions].sort((a, b) => b.attemptCount - a.attemptCount);
    const mostAttempted = sortedByAttempts[0];

    // Find problematic interactions (< 50% success rate)
    const problematic = interactions.filter((i) => i.successRate < 50);

    return {
      totalInteractions,
      totalAttempts,
      avgSuccessRate,
      problematicCount: problematic.length,
      best,
      worst,
      mostAttempted,
    };
  }, [interactions]);

  // Get color based on success rate
  const getColor = (rate: number): { bg: string; text: string; icon: string } => {
    if (rate >= 80) return { bg: "bg-green-100", text: "text-green-700", icon: "text-green-500" };
    if (rate >= 50)
      return { bg: "bg-yellow-100", text: "text-yellow-700", icon: "text-yellow-500" };
    return { bg: "bg-red-100", text: "text-red-700", icon: "text-red-500" };
  };

  const renderInteractionRow = (
    interaction: InteractionStat,
    label: string,
    iconType: "best" | "worst" | "mostAttempted",
  ) => {
    const colors = getColor(interaction.successRate);

    return (
      <button
        key={`${iconType}-${interaction.contentId}`}
        onClick={() => onInteractionClick?.(interaction.contentId, interaction.contentName)}
        className={`w-full flex items-center gap-3 p-2 rounded-lg ${colors.bg} hover:opacity-80 transition-opacity text-left`}
      >
        {/* Icon */}
        <div className={`flex-shrink-0 ${colors.icon}`}>
          {iconType === "best" && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
          )}
          {iconType === "worst" && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          )}
          {iconType === "mostAttempted" && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${colors.text} truncate`}>{interaction.contentName}</p>
          <p className="text-xs text-gray-500">
            {label}
            {iconType === "mostAttempted" && ` (${interaction.attemptCount})`}
          </p>
        </div>

        {/* Success rate */}
        <div className={`flex-shrink-0 text-sm font-bold ${colors.text}`}>
          {interaction.successRate.toFixed(0)}%
        </div>
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full">
        <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="space-y-2">
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      {/* Header */}
      <h3 className="text-sm font-medium text-gray-500 mb-3">
        {t("statsNew.module.topInteractions")}
      </h3>

      {!insights ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          {t("statsNew.emptyState.noInteractions")}
        </div>
      ) : (
        <>
          {/* Aggregated stats row */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{insights.totalInteractions}</p>
              <p className="text-xs text-gray-500">
                {t("statsNew.interactions.totalInteractions")}
              </p>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{insights.totalAttempts}</p>
              <p className="text-xs text-gray-500">{t("statsNew.interactions.attempts")}</p>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg group relative">
              <p
                className={`text-lg font-bold ${
                  insights.avgSuccessRate >= 80
                    ? "text-green-600"
                    : insights.avgSuccessRate >= 50
                    ? "text-yellow-600"
                    : "text-red-600"
                }`}
              >
                {insights.avgSuccessRate.toFixed(0)}%
              </p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                {t("statsNew.charts.weightedAverage")}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </p>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 w-48 text-center">
                {t("statsNew.charts.weightedAverageTooltip")}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          </div>

          {/* Problematic alert if any */}
          {insights.problematicCount > 0 && (
            <div className="flex items-center gap-2 p-2 mb-3 bg-red-50 border border-red-100 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-red-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-xs text-red-700">
                <span className="font-semibold">{insights.problematicCount}</span>{" "}
                {t("statsNew.interactions.problematic")}
              </p>
            </div>
          )}

          {/* Key interactions list */}
          <div className="space-y-2 flex-1">
            {/* Worst - show first to highlight problem */}
            {insights.worst &&
              renderInteractionRow(insights.worst, t("statsNew.module.lowestSuccess"), "worst")}

            {/* Most attempted - show engagement */}
            {insights.mostAttempted &&
              insights.mostAttempted.contentId !== insights.worst.contentId &&
              insights.mostAttempted.contentId !== insights.best.contentId &&
              renderInteractionRow(
                insights.mostAttempted,
                t("statsNew.interactions.attempts"),
                "mostAttempted",
              )}

            {/* Best - only show if success rate > 0% */}
            {insights.best &&
              insights.best.successRate > 0 &&
              insights.best.contentId !== insights.worst.contentId &&
              renderInteractionRow(insights.best, t("statsNew.module.highestSuccess"), "best")}
          </div>
        </>
      )}
    </div>
  );
};

export default TopInteractionsCard;
